import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ArrowRight, RotateCcw, ExternalLink, Search, LogIn } from 'lucide-react';

export const FEATURE_EMBED_COMMUNITY = true;
export const TYLER_COMMUNITY_URL = 'https://tylercommunity.tylertech.com/';
export const TYLER_COMMUNITY_SEARCH_BASE = 'https://tylercommunity.tylertech.com/search?q=';

interface TylerCommunityBrowserProps {
  className?: string;
}

export const TylerCommunityBrowser: React.FC<TylerCommunityBrowserProps> = ({ 
  className = "" 
}) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUrl, setCurrentUrl] = useState(TYLER_COMMUNITY_URL);
  const [navigationStack, setNavigationStack] = useState<string[]>([TYLER_COMMUNITY_URL]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const watchdog = setTimeout(() => {
      if (isLoading) {
        setIsBlocked(true);
        setIsLoading(false);
      }
    }, 8000);

    const handleLoad = () => {
      setIsLoading(false);
      clearTimeout(watchdog);
    };

    const iframe = frameRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleLoad);
      return () => {
        iframe.removeEventListener('load', handleLoad);
        clearTimeout(watchdog);
      };
    }

    return () => clearTimeout(watchdog);
  }, [isLoading]);

  const handleBack = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const url = navigationStack[newIndex];
      setCurrentIndex(newIndex);
      setCurrentUrl(url);
      if (frameRef.current) {
        frameRef.current.src = url;
      }
    }
  };

  const handleForward = () => {
    if (currentIndex < navigationStack.length - 1) {
      const newIndex = currentIndex + 1;
      const url = navigationStack[newIndex];
      setCurrentIndex(newIndex);
      setCurrentUrl(url);
      if (frameRef.current) {
        frameRef.current.src = url;
      }
    }
  };

  const handleReload = () => {
    setIsLoading(true);
    setIsBlocked(false);
    if (frameRef.current) {
      frameRef.current.src = frameRef.current.src;
    }
  };

  const handleOpenInNewTab = () => {
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const searchUrl = `${TYLER_COMMUNITY_SEARCH_BASE}${encodeURIComponent(searchQuery.trim())}`;
      window.open(searchUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const navigateToUrl = (url: string) => {
    const newStack = navigationStack.slice(0, currentIndex + 1);
    newStack.push(url);
    setNavigationStack(newStack);
    setCurrentIndex(newStack.length - 1);
    setCurrentUrl(url);
    setIsLoading(true);
    setIsBlocked(false);
    if (frameRef.current) {
      frameRef.current.src = url;
    }
  };

  return (
    <div className={`h-full flex flex-col ${className}`}>
      <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white drop-shadow-sm">
            Tyler Community
          </h2>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleForward}
              disabled={currentIndex >= navigationStack.length - 1}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Go forward"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleReload}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Reload page"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleOpenInNewTab}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex items-center space-x-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Tyler Community..."
              className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Search
          </button>
        </form>
      </div>

      <div className="flex-1 glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-xl overflow-hidden relative">
        {isBlocked ? (
          <div className="h-full flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="mb-4">
                <ExternalLink className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Tyler Community can't be embedded here
              </h3>
              <p className="text-slate-300 mb-6">
                Due to site security settings, Tyler Community cannot be displayed in this frame. 
                Click the button below to open it in a new tab.
              </p>
              <button
                onClick={handleOpenInNewTab}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2 mx-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Open in new tab</span>
              </button>
            </div>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10">
                <div className="flex items-center space-x-3 text-white">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading Tyler Community...</span>
                </div>
              </div>
            )}
            <iframe
              ref={frameRef}
              src={TYLER_COMMUNITY_URL}
              className="w-full h-full rounded-xl"
              sandbox="allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              aria-label="Tyler Community (in-app browser)"
              title="Tyler Community"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TylerCommunityBrowser;
